import { useState } from 'react'
import { createPortal } from 'react-dom'

import type { TagestrennerAngaben, Tageswort } from '../../api/types'
import { Restzeichen } from '../../components/Restzeichen'
import { useEscape } from '../../hooks/useEscape'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { trennerTag } from '../../lib/ortszeit'
import { useTexte } from '../../texte'

/**
 * The sheet of a day separator (#113, 1.85.0): date, count, word, and the
 * explaining line -- with the preview of what will stand in the chat.
 *
 * One sheet for two roads (#130): a new separator from the plus menu --
 * at the end or inserted before or after a chosen post -- and the change of
 * one that is there. What differs is the proposal that comes in and what
 * `onWahl` does with the answer; the fields are the same.
 *
 * Built like `Tagwahl` and `Rueckfrage`: on `document.body`, Escape and a
 * tap on the background say no, the safe way has the focus. Nothing is
 * sent from here -- the caller sends the separator as a post of its own.
 */
export function Tagestrennerwahl({
  vorgabe,
  aendern = false,
  nachnummerieren = false,
  laeuft = false,
  fehler = null,
  onWahl,
  onAbbruch,
}: {
  vorgabe: TagestrennerAngaben
  /** Changing one that is there: the sheet says so in its title and button. */
  aendern?: boolean
  /**
   * The proposal for "count the following on" (#130): ticked by the server
   * when the proposed number stands again further on. Only for a new one.
   */
  nachnummerieren?: boolean
  /** Sending: the button waits (a separator goes out at once, #130). */
  laeuft?: boolean
  /** What the server said no to, in words -- the sheet stays open. */
  fehler?: string | null
  onWahl: (angaben: TagestrennerAngaben, nachnummerieren: boolean) => void
  onAbbruch: () => void
}) {
  const t = useTexte()
  const texte = t.chat.tagestrenner
  const grenzen = useTextgrenzen()
  const [tag, setTag] = useState(vorgabe.day)
  const [nummer, setNummer] = useState(String(vorgabe.number))
  const [wort, setWort] = useState<Tageswort>(vorgabe.word)
  const [note, setNote] = useState(vorgabe.note)
  const [weiterzaehlen, setWeiterzaehlen] = useState(nachnummerieren)
  useEscape(onAbbruch)

  const zahl = Number.parseInt(nummer, 10)
  const gueltig = /^\d{4}-\d{2}-\d{2}$/.test(tag) && Number.isInteger(zahl) && zahl >= 1 && zahl <= 9999
    && [...note.trim()].length <= grenzen.day_separator_note
  const { datum, wochentag } = trennerTag(tag)
  const vorschau = gueltig ? texte.zeile(datum, texte.zaehler(wort, zahl), wochentag) : ''

  function uebernehmen() {
    if (!gueltig) return
    onWahl({ day: tag, number: zahl, word: wort, note: note.trim() }, !aendern && weiterzaehlen)
  }

  return createPortal(
    <div
      className="rueckfrage"
      role="dialog"
      aria-modal="true"
      aria-label={aendern ? texte.titelAendern : texte.titelNeu}
      onClick={onAbbruch}
      data-tagestrennerwahl=""
    >
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{aendern ? texte.titelAendern : texte.titelNeu}</p>
        <div className="bogen__rolle">
          <div className="tagwahl__zeiten">
            <label className="field field--dicht">
              <span className="field__label">{texte.tag}</span>
              <input className="field__input" type="date" value={tag} onChange={(event) => setTag(event.target.value)} data-tagestrenner-tag="" />
            </label>
            <label className="field field--dicht">
              <span className="field__label">{texte.nummer}</span>
              <input className="field__input" type="number" inputMode="numeric" min={1} max={9999} value={nummer} onChange={(event) => setNummer(event.target.value)} data-tagestrenner-nummer="" />
            </label>
          </div>
          <label className="field field--dicht">
            <span className="field__label">{texte.wort}</span>
            <select className="field__input" value={wort} onChange={(event) => setWort(event.target.value as Tageswort)} data-tagestrenner-wort="">
              <option value="tag">{texte.wortTag}</option>
              <option value="day">{texte.wortDay}</option>
            </select>
          </label>
          <label className="field field--dicht">
            <span className="field__label">{texte.erlaeuterung}</span>
            <input
              className="field__input"
              type="text"
              value={note}
              placeholder={texte.erlaeuterungBeispiel}
              onChange={(event) => setNote(event.target.value)}
              data-tagestrenner-note=""
            />
          </label>
          <Restzeichen wert={note} grenze={grenzen.day_separator_note} />
          {/* The preview is the line as the chat will show it -- the one
              place a person sees that "2" and "day" make "Day 2". */}
          <p className="leise" data-tagestrenner-vorschau="">{vorschau}</p>
          {!aendern && (
            <label className="choice">
              <input
                type="checkbox"
                checked={weiterzaehlen}
                onChange={(event) => setWeiterzaehlen(event.target.checked)}
                data-tagestrenner-nachnummerieren=""
              />
              <span>{texte.nachnummerieren}</span>
            </label>
          )}
          {fehler !== null && (
            <p className="notice" role="alert" data-tagestrenner-fehler="">
              {fehler}
            </p>
          )}
        </div>
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onAbbruch} autoFocus>
            {t.allgemein.abbrechen}
          </button>
          <button type="button" className="button button--small" onClick={uebernehmen} disabled={!gueltig || laeuft} data-tagestrenner-ok="">
            {aendern ? texte.speichern : texte.uebernehmen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
